/**
 * RiskRatingSubTab — the client's stored KYC risk assessment (TASK-229/237, rewritten TASK-926).
 *
 * What this file used to be: a gauge reading 65, five weighted "risk factors" with sub-factors
 * ("Sanctions — Pending review", "Adverse Media — 2 hits pending") and a twelve-point score
 * history, ALL literals in the file, mounted with no `clientId`. Recalculate flipped a local
 * spinner for 1.5 s and changed nothing. It described a client who did not exist, and it looked
 * exactly like a real assessment.
 *
 * The weighted five-factor model and the history chart are DELETED rather than wired up,
 * because the server has neither: `validation_service.calculate_risk_score` is additive, and
 * no score history is stored anywhere. Inventing weights in the UI is how the old file came to
 * assert a methodology nothing implements. If the product wants weights, that is a backend
 * change (TASK-926's own instruction), and a UI literal is not a shortcut to it.
 *
 * What is rendered now comes from `kyc_profiles`: the stored score, level, the scorer's own
 * contributing factors and recommended actions, and the two review dates. Recalculate is a
 * real POST that re-scores from the stored profile, writes the result — including
 * `next_review_date`, the clock the re-profile detector reads — and returns what it wrote.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  AlertOctagon,
  XOctagon,
  RefreshCw,
  Info,
} from 'lucide-react';
import kycService, { StoredRiskAssessment } from '../../../services/kycService';

type RiskLevel = StoredRiskAssessment['risk_level'];

const levelConfig: Record<RiskLevel, {
  label: string; color: string; bgColor: string; barColor: string; icon: typeof CheckCircle;
}> = {
  low: { label: 'Low Risk', color: 'text-green-700', bgColor: 'bg-green-50', barColor: 'bg-green-500', icon: CheckCircle },
  medium: { label: 'Medium Risk', color: 'text-amber-700', bgColor: 'bg-amber-50', barColor: 'bg-amber-500', icon: AlertTriangle },
  high: { label: 'High Risk', color: 'text-orange-700', bgColor: 'bg-orange-50', barColor: 'bg-orange-500', icon: AlertOctagon },
  critical: { label: 'Critical Risk', color: 'text-red-700', bgColor: 'bg-red-50', barColor: 'bg-red-500', icon: XOctagon },
};

const formatDate = (value: string | null): string =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

/** The scorer's own two lists, kept separate because they answer different questions: what
 *  drove the score, and what the firm is expected to do about it. */
const FactorList: React.FC<{ title: string; items: string[]; empty: string }> = ({ title, items, empty }) => (
  <div>
    <h4 className="mb-2 text-sm font-semibold text-gray-900">{title}</h4>
    {items.length === 0 ? (
      <p className="text-sm text-gray-500">{empty}</p>
    ) : (
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-sm text-gray-700">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

/** The structured half of `risk_factors` — whatever the scorer keyed, minus the two narrative
 *  lists rendered above. Rendered generically: the UI does not hold its own table of which
 *  factors exist, so a new one added server-side appears here without a frontend change. */
const StoredFactors: React.FC<{ factors: Record<string, unknown> }> = ({ factors }) => {
  const entries = Object.entries(factors).filter(
    ([key]) => key !== 'contributing_factors' && key !== 'recommended_actions',
  );
  if (entries.length === 0) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 className="mb-3 text-sm font-semibold text-gray-900">Assessed factors</h4>
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {entries.map(([key, value]) => (
          <div key={key} className="flex justify-between gap-3 border-b border-gray-100 py-1.5 text-sm">
            <dt className="text-gray-500">{key.replace(/_/g, ' ')}</dt>
            <dd className="font-medium text-gray-900">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export const RiskRatingSubTab: React.FC<{ clientId?: string }> = ({ clientId }) => {
  const [assessment, setAssessment] = useState<StoredRiskAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  // Distinguished states, because they are different facts: `noProfile` is "this client has no
  // KYC profile to score", `error` is "we could not find out". Collapsing them would let a
  // failed request read as a client with nothing on file.
  const [noProfile, setNoProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    setNoProfile(false);
    // Cleared BEFORE the fetch: switching client would otherwise render the previous client's
    // score under the new client's name for the length of the request. A risk rating attributed
    // to the wrong person is the one thing this tab must never do, even for 300ms.
    setAssessment(null);
    try {
      setAssessment(await kycService.getRiskAssessment(clientId));
    } catch (err: any) {
      setAssessment(null);
      if (err?.response?.status === 404) setNoProfile(true);
      else setError('The risk assessment could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  const recalculate = async () => {
    if (!clientId) return;
    setRecalculating(true);
    setError(null);
    try {
      setAssessment(await kycService.recalculateRiskAssessment(clientId));
    } catch (err: any) {
      if (err?.response?.status === 404) setNoProfile(true);
      else setError('The recalculation did not complete, so the stored score is unchanged.');
    } finally {
      setRecalculating(false);
    }
  };

  if (!clientId) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        Open a client to see their risk rating.
      </div>
    );
  }

  if (loading && !assessment) {
    return <div className="h-40 animate-pulse rounded-lg bg-gray-100" aria-hidden />;
  }

  if (noProfile) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          This client has no active KYC profile, so there is no risk rating to show. Complete
          the KYC profile first — a score cannot be assessed from nothing.
        </span>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        {error}{' '}
        <button type="button" onClick={() => void load()} className="font-medium underline">
          Try again
        </button>
      </div>
    );
  }

  if (!assessment) return null;

  const config = levelConfig[assessment.risk_level] ?? levelConfig.medium;
  const Icon = config.icon;
  const contributing = assessment.risk_factors?.contributing_factors ?? [];
  const recommended = assessment.risk_factors?.recommended_actions ?? [];

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Overall Risk Score</h3>
          <button
            type="button"
            onClick={() => void recalculate()}
            disabled={recalculating}
            className="flex items-center gap-2 rounded-lg border border-kielis-navy px-4 py-2 text-sm text-kielis-navy hover:bg-kielis-gold/10 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>

        <div className="mb-6 text-center">
          <div className={`mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2 ${config.bgColor}`}>
            <Icon className={`h-5 w-5 ${config.color}`} />
            <span className={`font-semibold ${config.color}`}>{config.label}</span>
          </div>

          <div className="mb-2 text-5xl font-bold text-gray-900">
            {assessment.risk_score}
            <span className="text-2xl font-normal text-gray-400"> / 100</span>
          </div>

          <div className="mx-auto mb-4 w-full max-w-md">
            <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-4 rounded-full transition-all duration-500 ${config.barColor}`}
                style={{ width: `${Math.min(100, Math.max(0, assessment.risk_score))}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-8 border-t border-gray-100 pt-4 text-sm text-gray-600">
          <div>
            <span className="text-gray-500">Last assessed:</span>
            <span className="ml-2 font-medium">{formatDate(assessment.last_review_date)}</span>
          </div>
          <div>
            {/* The clock the re-profile detector reads (TASK-926) — shown because an advisor
                acting on this score should see when it stops being current. */}
            <span className="text-gray-500">Next review due:</span>
            <span className="ml-2 font-medium">{formatDate(assessment.next_review_date)}</span>
          </div>
        </div>
      </div>

      <StoredFactors factors={assessment.risk_factors ?? {}} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <FactorList
            title="What drove this score"
            items={contributing}
            empty="Nothing in the profile raised the score above its base."
          />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <FactorList
            title="Recommended actions"
            items={recommended}
            empty="No additional due diligence is recommended at this rating."
          />
        </div>
      </div>
    </div>
  );
};

export default RiskRatingSubTab;
